<template>
<form class="pt-2">
    <div class="form-row">
        <div class="col-6">
            <form-input :label="formLabels.filing_id" v-model="formData.filing_id" :errors="errors.filing_id" :type="usesEIN ? 'ein' : 'text'" :maxlength="40" />
        </div>
        <div class="col-6">
            <form-input label="Exempt" v-model="formData.is_exempt" :errors="errors.is_exempt" type="select" :noblank="true" :options="[{text: 'Yes', value: true}, {text: 'No', value: false}]" />
        </div>
    </div>

    <p class="text-danger" v-show="formData.is_exempt == 'true'"><i class="far fa-fw fa-skull-crossbones" style="font-size: 1.5rem"></i> This tax will NOT be withheld for any employees of this company.</p>

    <div v-if="hasRate">
        <div class="d-flex align-items-center mt-2">
            <a href class="mr-auto text-decoration-none" @click.prevent="sectionExpanded = !sectionExpanded">
                <h5 class="mb-0 d-inline">Tax Rates</h5>
                <i :class="['far', 'fa-fw', sectionExpanded ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
            </a>
            <button v-if="sectionExpanded" type="button" class="btn btn-sm btn-outline-primary" @click.prevent="addBlock()">
                <i class="far fa-fw fa-plus"></i> Add Rate Change
            </button>
        </div>

        <template v-if="sectionExpanded">
            <div v-for="blockError in blockErrors" :key="blockError" class="text-danger mt-1">{{ blockError }}</div>

            <div v-for="(block, index) in blocks" :key="block.key" class="border rounded p-2 mt-2">
                <div class="d-flex justify-content-end" style="margin-bottom: -0.5rem">
                    <button
                        v-if="!block.editing"
                        type="button"
                        class="btn btn-sm btn-link py-0"
                        title="Edit"
                        @click.prevent="block.editing = true"
                    >
                        <i class="far fa-fw fa-pencil"></i>
                    </button>
                    <button
                        type="button"
                        class="btn btn-sm btn-link text-danger py-0"
                        title="Delete"
                        @click.prevent="removeBlock(index)"
                    >
                        <i class="far fa-fw fa-trash-alt"></i>
                    </button>
                </div>

                <div class="form-row">
                    <div class="col-6">
                        <label class="text-small">Effective Date</label>
                        <form-input v-model="block.effective_on" type="date" :placeholder="index == 0 && !block.effective_on ? 'Initial' : ''" :disabled="!block.editing" />
                    </div>
                    <div class="col-6">
                        <label class="text-small">Tax Rate (%)</label>
                        <form-input v-model="block.rate" type="decimal" :maxlength="8" :disabled="!block.editing" />
                    </div>
                </div>

            </div>
        </template>
    </div>

    <form-errors :errors="formErrors" />
</form>
</template>

<script>
import FormMixin from '@/mixins/Form'

let blockKeyCounter = 0

export default {
    props: {
        uniqueTaxId: {},
        value: {},
        hasRate: {},
        usesEIN: {},
    },
    mixins: [FormMixin, ],
    data() {
        const initialValue = this.value || {}
        return {
            formData: {
                filing_id: initialValue.filing_id || '',
                is_exempt: (initialValue.is_exempt || false) ? 'true' : 'false',  // The select form input uses strings for values
            },
            blocks: this.makeInitialBlocks(),
            sectionExpanded: true,
            blockErrors: [],
            formRules: {
                filing_id: {presence: {allowEmpty: false}, },
                is_exempt: {presence: {allowEmpty: false}, },
            },
            formLabels: {
                filing_id: this.usesEIN ? 'EIN' : 'Filing ID',
                is_exempt: 'Exempt',
            },
        }
    },
    methods: {
        makeInitialBlocks() {
            const initialValue = this.value || {}
            const parameterSets = (initialValue.parameter_sets || []).slice()

            parameterSets.sort((a, b) => {
                if (!a.effective_on) {
                    return -1
                }
                if (!b.effective_on) {
                    return 1
                }
                return a.effective_on < b.effective_on ? -1 : 1
            })

            const blocks = parameterSets.map(parameterSet => {
                return {
                    key: `existing-${parameterSet.id}`,
                    id: parameterSet.id,
                    effective_on: parameterSet.effective_on,
                    rate: parameterSet.rate,
                    // Saved blocks start read-only; the pencil button unlocks them.
                    editing: false,
                }
            })

            if (!blocks.length) {
                blocks.push(this.makeBlankBlock())
            }

            return blocks
        },
        makeBlankBlock() {
            blockKeyCounter += 1
            return {
                key: `new-${blockKeyCounter}`,
                id: null,
                effective_on: null,
                rate: null,
                editing: true,
            }
        },
        removeBlock(index) {
            this.blocks.splice(index, 1)
        },
        addBlock() {
            const latest = this.blocks.length ? this.blocks[this.blocks.length - 1] : null
            const block = this.makeBlankBlock()
            if (latest) {
                // A new block usually tweaks the previous rate, so copy the
                // latest one; the date starts blank on purpose.
                block.rate = latest.rate
            }
            this.blocks.push(block)
        },
        validate(fields=[]) {
            const isValid = FormMixin.methods.validate.call(this, fields)

            this.blockErrors = []
            if (this.hasRate) {
                let previousDate = null
                this.blocks.forEach((block, index) => {
                    if (!block.effective_on && index > 0) {
                        this.blockErrors.push(`Block ${index + 1}: Effective Date cannot be blank.`)
                    }
                    if (block.effective_on && previousDate && block.effective_on < previousDate) {
                        this.blockErrors.push(`Block ${index + 1}: Effective Date cannot be earlier than ${previousDate}.`)
                    }
                    if (block.effective_on) {
                        previousDate = block.effective_on
                    }
                })
            }

            if (this.blockErrors.length) {
                this.sectionExpanded = true
            }

            return isValid && !this.blockErrors.length
        },
        setErrors(errors) {
            const parameterSetErrors = errors.parameter_sets
            if (parameterSetErrors) {
                this.blockErrors = parameterSetErrors
                this.sectionExpanded = true
            }
            Object.assign(this.errors, errors)
        },
        getFormData() {
            const data = JSON.parse(JSON.stringify(this.formData))
            data.is_exempt = (data.is_exempt == 'true')
            if (this.usesEIN) {
                // Format EIN
                const digits = data.filing_id.replace(/\D/g, '').slice(0, 9)
                data.filing_id = `${digits.slice(0, 2)}-${digits.slice(2)}`
            }

            if (this.hasRate) {
                data.parameter_sets = this.blocks.map(block => {
                    return {
                        id: block.id,
                        effective_on: block.effective_on || null,
                        rate: (block.rate === '' || block.rate === null) ? null : block.rate,
                    }
                })
            }

            return data
        },
        setFilingID(filingId) {
            this.formData.filing_id = filingId
        },
    },
}

</script>
